/**
 * RBAC — สิทธิ์และขอบเขตข้อมูล
 *
 * ไฟล์นี้เป็นแหล่งความจริงเดียวของเรื่องสิทธิ์ ใช้ทั้งฝั่ง Worker (บังคับจริง)
 * และฝั่ง frontend (ซ่อน/แสดงเมนู) — frontend ใช้เพื่อ UX เท่านั้น
 * การบังคับสิทธิ์จริงเกิดที่ Worker เสมอ
 */

export const ROLES = [
  'super_admin',
  'admin',
  'finance',
  'supervisor',
  'field_officer',
  'viewer',
] as const;

export type Role = (typeof ROLES)[number];

export const ROLE_LABEL_TH: Record<Role, string> = {
  super_admin: 'ผู้ดูแลระบบสูงสุด',
  admin: 'ผู้ดูแลระบบ',
  finance: 'เจ้าหน้าที่การเงิน',
  supervisor: 'หัวหน้าทีม',
  field_officer: 'เจ้าหน้าที่ภาคสนาม',
  viewer: 'ผู้อ่านอย่างเดียว',
};

/** สิทธิ์ระดับปฏิบัติการ — ตั้งชื่อตาม "ทำอะไรได้" ไม่ใช่ "เป็นใคร" */
export const PERMISSIONS = [
  'staff:read',
  'staff:write',
  'staff:set_role',
  'staff:unlink_line',

  'beneficiary:read',
  'beneficiary:write',
  'beneficiary:delete',
  'beneficiary:read_sensitive',
  'beneficiary:decrypt_nid',
  'beneficiary:anonymize',

  'case:read',
  'case:write',
  'case:approve_l1',
  'case:approve_l2',
  'case:approve_l3',

  'visit:read',
  'visit:write',
  'visit:review',

  'form:read',
  'form:write',

  'donor:read',
  'donor:write',
  'donation:read',
  'donation:write',
  'donation:verify',
  'receipt:issue',

  'report:read',
  'report:export',

  'settings:write',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const FIELD_OFFICER: Permission[] = [
  'beneficiary:read',
  'beneficiary:write',
  'beneficiary:read_sensitive',
  'case:read',
  'case:write',
  'visit:read',
  'visit:write',
  'form:read',
  'donation:write',
  'report:read',
];

const SUPERVISOR: Permission[] = [
  ...FIELD_OFFICER,
  'staff:read',
  'case:approve_l1',
  'visit:review',
];

const FINANCE: Permission[] = [
  'beneficiary:read',
  'case:read',
  'donor:read',
  'donor:write',
  'donation:read',
  'donation:write',
  'donation:verify',
  'receipt:issue',
  'report:read',
  'report:export',
];

const ADMIN: Permission[] = [
  'staff:read',
  'staff:write',
  'staff:unlink_line',
  'beneficiary:read',
  'beneficiary:write',
  'beneficiary:delete',
  'beneficiary:read_sensitive',
  'case:read',
  'case:write',
  'case:approve_l1',
  'case:approve_l2',
  'visit:read',
  'visit:write',
  'visit:review',
  'form:read',
  'form:write',
  'donor:read',
  'donation:read',
  'report:read',
  'report:export',
  'settings:write',
];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  super_admin: PERMISSIONS,
  admin: ADMIN,
  finance: FINANCE,
  supervisor: SUPERVISOR,
  field_officer: FIELD_OFFICER,
  viewer: ['beneficiary:read', 'case:read', 'visit:read', 'report:read'],
};

export function can(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function canAll(role: Role, permissions: Permission[]): boolean {
  return permissions.every((p) => can(role, p));
}

/**
 * ขอบเขตข้อมูลที่ role นั้นมองเห็น
 *   all    — ทั้งองค์กร
 *   team   — เฉพาะผู้รับความช่วยเหลือที่เจ้าหน้าที่ในทีมเดียวกันดูแล
 *   own    — เฉพาะที่ตัวเองดูแล หรืออยู่ในพื้นที่รับผิดชอบของตัวเอง
 *   none   — เข้าถึงรายบุคคลไม่ได้เลย ต้องผ่าน /api/reports ที่รวมยอดแล้วเท่านั้น
 */
export type DataScope = 'all' | 'team' | 'own' | 'none';

export const ROLE_SCOPE: Record<Role, DataScope> = {
  super_admin: 'all',
  admin: 'all',
  finance: 'all',
  supervisor: 'team',
  field_officer: 'own',
  viewer: 'none',
};

/** ระดับการอนุมัติสูงสุดที่ role นี้ทำได้ (0 = อนุมัติไม่ได้) */
export function maxApprovalLevel(role: Role): 0 | 1 | 2 | 3 {
  if (can(role, 'case:approve_l3')) return 3;
  if (can(role, 'case:approve_l2')) return 2;
  if (can(role, 'case:approve_l1')) return 1;
  return 0;
}
